import {
	ReactElement,
	Ref,
	RefObject,
	useCallback,
	useContext,
	useMemo,
	useRef,
	useState,
	KeyboardEvent,
	useEffect
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import {
	KeyBoardListNavigationContext,
	KeyboardListNavigationItemOptions,
	useKeyboardListNavigation,
	useKeyboardListNavigationItem,
	useResizeObserver
} from "client/Hooks";
import { ActionListCheckbox } from "../ActionList/ActionListCheckbox";
import { DropOverPreview } from "../DropOverPreview/DropOverPreview";
import {
	convertArbitraryValueForTrackingId,
	EMPTY_ARRAY,
	generateTrackingId,
	isNotNil,
	isNotNilOrEmpty,
	setTrackingId
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import styles from "./MultiSelect.module.less";
import classNames from "classnames";
import { DropOver } from "../DropOver/DropOver";
import { ActionListBox } from "../ActionListBox/ActionListBox";
import { ActionList } from "../ActionList/ActionList";
import { EmptyValuePlaceholder } from "../Select/Select";
import { NoUniqueInputIdContext, UniqueInputIdContext } from "client/Contexts/UniqueInputIdContext";
import {
	combineAttachmentKinds,
	ControlAttachmentContext,
	ControlAttachmentOptions,
	NO_CONTROL_ATTACHMENTS
} from "../ControlAttachment";
import { Guid, newGuid } from "client_legacy/ndaw/guid";
import { Key } from "client/Misc/commonTypes";
import { ActionListCollapsibleSection } from "../ActionList/ActionListCollapsibleSection";
import { contains, normalizeForSearch } from "client/Data/searching";
import { SearchInputForm, SearchInputFormHandle } from "../SearchInputForm/SearchInputForm";
import { SearchPlaceholder } from "../SearchInput/SearchPlaceholder";
import { NoResults } from "../NoResults/NoResults";
import { tryResolveKeyboardKey } from "client/Misc/keyboardUtils";
import { KeyEventArguments } from "../TextInput/TextInput";

export type MultiSelectOption<TValue> = {
	text: string;
	value: TValue;
};

export type MultiSelectBranch<TValue> = {
	text: string;
	uniqueId: Guid;
	children: readonly MultiSelectItem<TValue>[];
};

export type MultiSelectItem<TValue> =
	| MultiSelectOption<TValue>
	| MultiSelectBranch<TValue>;

export type MultiSelectProps<TValue> = {
	value: readonly TValue[];
	onChange(value: readonly TValue[]): void;
	options: readonly MultiSelectItem<TValue>[];
	hasBusyIndicator?: boolean;
	isDisabled?: boolean;
	trackingId: string;
	attachedOn?: ControlAttachmentOptions;
	placeholder?: string;
	menuTitle: string;
	isInvalid?: boolean;
	areEqual?: (a: TValue, b: TValue) => boolean;
} & ({
	doesSupportFiltering?: false;
} | {
	doesSupportFiltering: true;
	placeholderPropertyNames: readonly string[];
});

export const MultiSelect = <TValue,>(
	props: MultiSelectProps<TValue>
): ReactElement => {
	const {
		value,
		onChange,
		options,
		isDisabled = false,
		hasBusyIndicator,
		trackingId,
		attachedOn = EMPTY_ARRAY,
		placeholder,
		menuTitle,
		isInvalid = false,
		areEqual = defaultAreEqual,
		doesSupportFiltering
	} = props;

	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [dropOverId] = useState<Guid>(() => newGuid());
	const [collapsedItemIds, setCollapsedItemIds] = useState<Set<Guid>>(new Set());
	const [searchTerm, setSearchTerm] = useState<string>("");

	const previewRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);
	const ref: Ref<HTMLDivElement> = useRef<HTMLDivElement>(null);
	const searchRef: RefObject<SearchInputFormHandle | null> = useRef<SearchInputFormHandle>(null);
	const { width } = useResizeObserver(ref);
	const { id } = useContext(UniqueInputIdContext);

	const normalizedSearchTerm: string = useMemo(
		() => normalizeForSearch(searchTerm),
		[searchTerm]
	);

	const flattenItemsIdendifiers = useMemo(
		(): readonly ItemIdentifier<TValue>[] => {
			const flattenItems: ItemIdentifier<TValue>[] = [];
			getFlattenItemsIdentifiers(options, flattenItems);
			return flattenItems;
		},
		[options]
	);

	const filteredItems = useMemo(
		(): readonly MultiSelectItem<TValue>[] => getFilteredItems(options, normalizedSearchTerm),
		[options, normalizedSearchTerm]
	);

	const focusPreviewHandler = useCallback(
		(): void => {
			previewRef.current
				?.focus();
		},
		[]
	);

	const cancelHandler = useCallback(
		(): void => {
			setIsOpen(false);
			focusPreviewHandler();
		},
		[focusPreviewHandler]
	);

	const {
		contextData,
		focusFirstItemHandler,
		focusItemAtIndexHandler
	} = useKeyboardListNavigation({
		onCancel: cancelHandler,
		shouldCancelDirectlyFromItems: true,
		onFocusInput: () => {
			searchRef
				.current
				?.focusInput();
		}
	});

	const {
		attachedOn: attachedOnFromContext
	} = useContext(ControlAttachmentContext);

	const changeHandler = (item: MultiSelectOption<TValue>, becameChecked: boolean): void => {
		const copy: TValue[] = [...value];

		if (becameChecked) {
			copy.push(item.value);
		} else {
			const indexInValue: number = value.indexOf(item.value);
			copy.splice(indexInValue, 1);
		}

		onChange(copy);
	};

	const toggleCollapseHandler = useCallback(
		(item: MultiSelectBranch<TValue>): void => {
			setCollapsedItemIds(collapsedIds => {
				const newSet: Set<Guid> = new Set(collapsedIds);

				if (!newSet.delete(item.uniqueId)) {
					newSet.add(item.uniqueId);
				}

				return newSet;
			});
		},
		[]
	);

	const selectedOptions = useMemo(
		(): readonly MultiSelectOption<TValue>[] => {
			const result: MultiSelectOption<TValue>[] = [];

			findOptions(
				value,
				options,
				areEqual,
				result
			);

			return result;
		},
		[
			value,
			options,
			areEqual
		]
	);

	const getItemGlobalIndexHandler = useCallback(
		(item: MultiSelectItem<TValue>): number => findItemGlobalIndex(
			item,
			flattenItemsIdendifiers,
			areEqual
		),
		[flattenItemsIdendifiers, areEqual]
	);

	let previewText: string;
	let shouldShowPlaceholder: boolean;

	if (selectedOptions.length === 0) {
		previewText = localize("_None");
		shouldShowPlaceholder = isNotNilOrEmpty(placeholder);
	} else {
		previewText = selectedOptions.map(x => x.text)
			.join(", ");

		shouldShowPlaceholder = false;
	}

	const previewKeyDownHandler = useCallback(
		(e: KeyboardEvent): void => {
			switch (tryResolveKeyboardKey(e)) {
				case Key.ArrowDown:
				case Key.Enter:
				case Key.Space:
					e.preventDefault();
					setIsOpen(true);

					if (hasBusyIndicator) {
						return;
					}

					// Ensure the dropdown is mounted before focusing an item
					setTimeout(
						(): void => {
							if (searchRef.current) {
								searchRef
									.current
									.focusInput();

								return;
							}

							if (selectedOptions.length > 0) {
								focusItemAtIndexHandler(getItemGlobalIndexHandler(selectedOptions[0]));
							} else {
								focusFirstItemHandler();
							}
						},
						1
					);
					return;
				case Key.Escape:
				case Key.Tab:
					setIsOpen(false);
					return;
				default:
					return;
			}
		},
		[
			selectedOptions,
			hasBusyIndicator,
			focusItemAtIndexHandler,
			focusFirstItemHandler,
			getItemGlobalIndexHandler
		]
	);

	const listSearchKeyDownHandler = useCallback(
		(args: KeyEventArguments): boolean => {
			switch (args.key) {
				case Key.Escape:
				case Key.Tab:
					cancelHandler();
					return true;
				default:
					return false;
			}
		},
		[cancelHandler]
	);

	useEffect(
		(): void => {
			if (!isOpen) {
				setCollapsedItemIds(new Set());
				setSearchTerm("");
			}
		},
		[isOpen]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(searchTerm);
			setCollapsedItemIds(new Set());
		},
		[searchTerm]
	);

	const renderContent = (): ReactElement => {
		const itemsCount: number = filteredItems.length;

		if (itemsCount === 0 && !hasBusyIndicator && !searchTerm) {
			return <EmptyValuePlaceholder />;
		}

		return (
			<KeyBoardListNavigationContext.Provider value={contextData}>
				<div className={styles.content}>
					{doesSupportFiltering && (
						<div className={styles.searchHeader}>
							<SearchInputForm
								ref={searchRef}
								trackingId={generateTrackingId(trackingId, "__search")}
								onNavigateToResults={focusFirstItemHandler}
								onChange={setSearchTerm}
								icon="filter"
								placeholder={(
									<SearchPlaceholder
										kind="filter"
										properties={props.placeholderPropertyNames}
									/>
								)}
								onKeyDown={listSearchKeyDownHandler}
								hasMarginBottom={false}
							/>
						</div>
					)}
					<div className={styles.list}>
						<ActionList
							trackingId={generateTrackingId(trackingId, "__dropover")}
							width={typeof width !== "undefined" ? "fill" : "auto"}
						>
							{searchTerm && itemsCount === 0
								? (
									<NoResults
										icon="magnifying-glass"
										title={localize("_NoSearchResults")}
										message={localize("_PleaseTrySearchingWithAnotherTerm")}
										trackingId="no-search-results"
									/>
								)
								: (
									<NoUniqueInputIdContext>
										<List
											items={filteredItems}
											value={value}
											onChange={changeHandler}
											areEqual={areEqual}
											getItemGlobalIndex={getItemGlobalIndexHandler}
											indentionLevel={0}
											collapsedItemIds={collapsedItemIds}
											onToggleCollapse={toggleCollapseHandler}
											onFocusPreview={focusPreviewHandler}
											rootTrackingId={trackingId}
										/>
									</NoUniqueInputIdContext>
								)
							}
						</ActionList>
					</div>
				</div>
			</KeyBoardListNavigationContext.Provider>
		);
	};

	return (
		<ControlAttachmentContext.Provider value={NO_CONTROL_ATTACHMENTS}>
			<div
				{...setTrackingId(trackingId)}
				ref={ref}
			>
				<DropOverPreview
					isOpen={isOpen}
					onClick={() => setIsOpen((x) => !x)}
					ref={previewRef}
					isDisabled={isDisabled}
					isInvalid={isInvalid}
					id={id}
					attachedOn={combineAttachmentKinds(attachedOn, attachedOnFromContext)}
					onKeyDown={previewKeyDownHandler}
					ariaHaspopup="listbox"
					ariaControls={dropOverId}
					ariaLabel={menuTitle}
				>
					<div
						className={classNames(
							styles.preview,
							{
								[styles.preview_placeholder]: shouldShowPlaceholder
							}
						)}
					>
						{ shouldShowPlaceholder ? placeholder : previewText }
					</div>
				</DropOverPreview>

				{ isOpen && ref.current && (
					<DropOver
						target={ref.current}
						onClose={() => setIsOpen(false)}
						role="listbox"
						id={dropOverId}
						ariaMultiselectable
						ariaLabel={menuTitle}
					>
						<ActionListBox
							width={width}
							isHeightConstrainted
							hasBusyIndicator={hasBusyIndicator}
							menuTitle={menuTitle}
							trackingId={generateTrackingId(trackingId, "__dropover__wrapper")}
							responsiveMode={{
								kind: "auto",
								height: doesSupportFiltering
									? "fill"
									: undefined
							}}
						>
							{renderContent()}
						</ActionListBox>
					</DropOver>
				)}
			</div>
		</ControlAttachmentContext.Provider>
	);
};
MultiSelect.displayName = "MultiSelect";

type ListProps<TValue> = {
	items: readonly MultiSelectItem<TValue>[];
	value: readonly TValue[];
	onChange: (item: MultiSelectOption<TValue>, becameChecked: boolean) => void;
	areEqual: (a: TValue, b: TValue) => boolean;
	getItemGlobalIndex: (item: MultiSelectItem<TValue>) => number;
	indentionLevel: number;
	collapsedItemIds: Set<Guid>;
	onToggleCollapse: (item: MultiSelectBranch<TValue>) => void;
	onFocusPreview: () => void;
	rootTrackingId: string;
};

const List = <TValue, >(props: ListProps<TValue>): ReactElement => {
	const {
		value,
		items,
		rootTrackingId,
		indentionLevel,
		collapsedItemIds,
		getItemGlobalIndex,
		onChange,
		areEqual,
		onFocusPreview,
		onToggleCollapse
	} = props;

	return (
		<>
			{items.map((item, index) => {
				return (
					<ListItem
						key={index}
						item={item}
						value={value}
						indentionLevel={indentionLevel}
						collapsedItemIds={collapsedItemIds}
						getItemGlobalIndex={getItemGlobalIndex}
						onChange={onChange}
						areEqual={areEqual}
						onFocusPreview={onFocusPreview}
						onToggleCollapse={onToggleCollapse}
						rootTrackingId={rootTrackingId}
					/>
				);
			})}
		</>
	);
};

type ListItemProps<TValue> = {
	item: MultiSelectItem<TValue>;
	value: readonly TValue[];
	onChange: (item: MultiSelectOption<TValue>, becameChecked: boolean) => void;
	areEqual: (a: TValue, b: TValue) => boolean;
	indentionLevel: number;
	collapsedItemIds: Set<Guid>;
	getItemGlobalIndex: (item: MultiSelectItem<TValue>) => number;
	onToggleCollapse: (item: MultiSelectBranch<TValue>) => void;
	onFocusPreview: () => void;
	rootTrackingId: string;
};

const ListItem = <TValue,>(props: ListItemProps<TValue>): ReactElement => {
	const {
		item,
		value,
		indentionLevel,
		rootTrackingId,
		collapsedItemIds,
		onChange,
		areEqual,
		onFocusPreview,
		onToggleCollapse,
		getItemGlobalIndex
	} = props;

	const index = useMemo(
		(): number => getItemGlobalIndex(item),
		[item, getItemGlobalIndex]
	);

	const keyDownHandler = useCallback(
		(key: Key): void => {
			switch (key) {
				case Key.Enter:
					if (isOption(item)) {
						onChange(item, !value.some(x => areEqual(x, item.value)));
					} else {
						onToggleCollapse(item);
					}
					break;
				case Key.Space:
					if (isBranchItem(item)) {
						onToggleCollapse(item);
					} else {
						onChange(item, !value.some(x => areEqual(x, item.value)));
					}
					break;
				default:
					// Nothing to do
			}
		},
		[
			value,
			item,
			onToggleCollapse,
			onChange,
			areEqual
		]
	);

	const selectedItemsCount = useMemo(
		(): number | null => {
			if (!isBranchItem(item) || !collapsedItemIds.has(item.uniqueId)) {
				return null;
			}

			const result: MultiSelectOption<TValue>[] = [];

			findOptions(
				value,
				item.children,
				areEqual,
				result
			);

			return result.length;
		},
		[
			item,
			collapsedItemIds,
			value,
			areEqual
		]
	);

	const options = useMemo(
		(): KeyboardListNavigationItemOptions => ({
			itemIndex: index,
			executeKey: [
				Key.Enter,
				Key.Space
			],
			shouldCancelOnTab: true,
			onExecute: keyDownHandler
		}),
		[index, keyDownHandler]
	);

	const {
		ref,
		onKeyDown
	} = useKeyboardListNavigationItem(options);

	if (isOption(item)) {
		const isSelected: boolean = value.some(x => areEqual(x, item.value));

		return (
			<ActionListCheckbox
				ref={ref}
				onKeyDown={onKeyDown}
				tabIndex={-1}
				value={isSelected}
				text={item.text}
				role="option"
				onChange={checkedState => onChange(item, checkedState)}
				shouldNotAutoCloseContainingDropOver
				indentionLevel={indentionLevel}
				trackingId={generateTrackingId(rootTrackingId, `__item|${getItemValueTrackingId(item)}`)}
			/>
		);
	}

	const isCollapsed: boolean = collapsedItemIds.has(item.uniqueId);

	return (
		<ActionListCollapsibleSection
			ref={ref}
			header={item.text}
			suffix={
				isCollapsed
					&& selectedItemsCount
					&& selectedItemsCount > 0
						? `(${selectedItemsCount})`
						: undefined
			}
			trackingId={generateTrackingId(rootTrackingId, `__item|${item.uniqueId}`)}
			indentionLevel={indentionLevel}
			isCollapsed={isCollapsed}
			onToggle={() => onToggleCollapse(item)}
			onKeyDown={onKeyDown}
			tabIndex={-1}
			role="group"
			hasLine
		>
			<List
				items={item.children}
				value={value}
				onChange={onChange}
				areEqual={areEqual}
				getItemGlobalIndex={getItemGlobalIndex}
				indentionLevel={indentionLevel + 1}
				collapsedItemIds={collapsedItemIds}
				onToggleCollapse={onToggleCollapse}
				onFocusPreview={onFocusPreview}
				rootTrackingId={rootTrackingId}
			/>
		</ActionListCollapsibleSection>
	);
};
ListItem.displayName = "ListItem";

type ItemIdentifier<TValue> = {
	kind: "branch";
	uniqueId: Guid;
} | {
	kind: "option";
	value: TValue;
};

function defaultAreEqual<TValue>(a: TValue, b: TValue): boolean {
	return a === b;
}

function isOption<TValue>(item: MultiSelectItem<TValue>): item is MultiSelectOption<TValue> {
	return !isBranchItem(item);
}

function isBranchItem<TValue>(item: MultiSelectItem<TValue>): item is MultiSelectBranch<TValue> {
	return isNotNil((item as MultiSelectBranch<TValue>).children);
}

function getItemValueTrackingId<TValue>(item: MultiSelectOption<TValue>): string {
	return convertArbitraryValueForTrackingId(item.value);
}

function findOptions<TValue>(
	value: readonly TValue[],
	items: readonly MultiSelectItem<TValue>[],
	areEqual: (a: TValue, b: TValue) => boolean,
	result: MultiSelectOption<TValue>[]
): void {
	if (value.length === 0) {
		return;
	}

	for (const item of items) {
		if (isOption(item)) {
			if (value.some(x => areEqual(x, item.value))) {
				result.push(item);
			}
		} else {
			findOptions(
				value,
				item.children,
				areEqual,
				result
			);
		}
	}
}

function getFlattenItemsIdentifiers<TValue>(
	items: readonly MultiSelectItem<TValue>[],
	result: ItemIdentifier<TValue>[]
): void {
	for (const item of items) {
		result.push(isOption(item)
			? {
				kind: "option",
				value: item.value
			}
			: {
				kind: "branch",
				uniqueId: item.uniqueId
			}
		);

		if (isBranchItem(item)) {
			getFlattenItemsIdentifiers(item.children, result);
		}
	}
}

function findItemGlobalIndex<TValue>(
	item: MultiSelectItem<TValue>,
	flattenItemsIdendifiers: readonly ItemIdentifier<TValue>[],
	areEqual: (a: TValue, b: TValue) => boolean
): number {
	const index: number = flattenItemsIdendifiers
		.findIndex(
			x => isOption(item)
				? x.kind === "option" && areEqual(item.value, x.value)
				: x.kind === "branch" && (x.uniqueId === item.uniqueId)
		);

	if (index === -1) {
		throw new Error("Couldn't find item global index");
	}

	return index;
}

function getFilteredItems<TValue>(
	items: readonly MultiSelectItem<TValue>[],
	searchTerm: string,
): readonly MultiSelectItem<TValue>[] {
	const result: MultiSelectItem<TValue>[] = [];

	for (const item of items) {
		if (isOption(item) && contains(item.text, searchTerm)) {
			result.push(item);
		}

		if (isBranchItem(item)) {
			const children: readonly MultiSelectItem<TValue>[] = getFilteredItems(item.children, searchTerm);

			if (children.length > 0) {
				result.push({
					...item,
					children
				});
			}
		}
	}

	return result;
}